import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import {
  ARTICLE_TOPICS,
  ARTICLES_UI,
  articleCrumbs,
  formatMonthYear,
  readingMinutes,
  type ArticleLang,
  type ArticleTopic,
  type Crumb,
} from '../../articles';

// Building blocks shared by the articles overview (pages/GuidesOverview.tsx),
// the article pages and the Quick Click-Throughs. Styling is the site's
// existing vocabulary (btn-* classes, pstax-* colors, the guide pages'
// breadcrumb/CTA markup) — nothing here introduces a new look.

export const Breadcrumb: React.FC<{ crumbs: Crumb[]; lang: ArticleLang; className?: string }> = ({
  crumbs,
  lang,
  className = 'text-sm text-pstax-gray mb-6',
}) => (
  <nav aria-label={ARTICLES_UI[lang].breadcrumbAria} className={className}>
    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {crumbs.map((c, i) => (
        <React.Fragment key={`${c.label}-${i}`}>
          {i > 0 && <li aria-hidden="true">›</li>}
          {c.path ? (
            <li>
              <Link to={c.path} className="hover:text-pstax-link">
                {c.label}
              </Link>
            </li>
          ) : (
            <li className="text-pstax-navy font-semibold" aria-current="page">
              {c.label}
            </li>
          )}
        </React.Fragment>
      ))}
    </ol>
  </nav>
);

// "Keine Zeit? Zum Quick Click-Through →" — directly under an article's H1.
export const QuickLink: React.FC<{ topic: ArticleTopic; lang: ArticleLang }> = ({ topic, lang }) => (
  <p className="mb-6">
    <Link
      to={topic[lang].shortPath}
      className="text-sm font-semibold text-pstax-link hover:underline underline-offset-2"
    >
      {ARTICLES_UI[lang].toQuick}
    </Link>
  </p>
);

// Dark box at the end of the overview and of every article — same look as
// the guide pages' existing CTA box.
export const FitCheckBox: React.FC<{ lang: ArticleLang; className?: string }> = ({ lang, className = 'mt-14' }) => {
  const ui = ARTICLES_UI[lang];
  return (
    <div className={`${className} bg-pstax-navy text-white rounded-3xl p-8 sm:p-10`}>
      <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3 text-white">{ui.fitCheckTitle}</h2>
      <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">{ui.fitCheckText}</p>
      <Link to={ui.fitCheckPath} className="btn-accent inline-block" data-cta-location="section">
        {ui.fitCheckCta}
      </Link>
    </div>
  );
};

const TopicTag: React.FC<{ topic: ArticleTopic; lang: ArticleLang }> = ({ topic, lang }) => {
  const Icon = topic.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.2em] text-pstax-link">
      <Icon size={14} aria-hidden="true" />
      {topic[lang].tag}
    </span>
  );
};

// One tile on the overview (and in "Weitere Themen").
export const ArticleCard: React.FC<{ topic: ArticleTopic; lang: ArticleLang; headingLevel?: 'h2' | 'h3' }> = ({
  topic,
  lang,
  headingLevel = 'h2',
}) => {
  const ui = ARTICLES_UI[lang];
  const v = topic[lang];
  const Heading = headingLevel;
  return (
    <article className="glass-card p-6 sm:p-8 flex flex-col h-full">
      <TopicTag topic={topic} lang={lang} />
      <Heading className="mt-4 mb-3 text-xl sm:text-2xl font-display font-bold text-pstax-navy leading-snug">
        <Link to={v.path} className="hover:text-pstax-link">
          {v.h1}
        </Link>
      </Heading>
      <p className="text-pstax-gray leading-relaxed mb-4">{v.description}</p>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-pstax-gray mb-6">
        <span>
          {ui.updated} <time dateTime={v.dateModified}>{formatMonthYear(v.dateModified, lang)}</time>
        </span>
        <span aria-hidden="true">·</span>
        <span className="inline-flex items-center gap-1">
          <Clock size={14} aria-hidden="true" />
          {ui.readingTime(readingMinutes(v))}
        </span>
      </p>
      <div className="mt-auto flex flex-wrap items-center gap-3">
        <Link to={v.path} className="btn-primary !px-6 !py-3 text-[15px] inline-flex items-center gap-2">
          {ui.readArticle}
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
        <Link
          to={v.shortPath}
          className="btn-outline !px-6 !py-3 text-[15px] inline-flex flex-col items-start leading-tight"
        >
          <span>{ui.quick}</span>
          <span className="text-xs font-medium text-pstax-gray">{ui.interactiveSteps(topic.steps)}</span>
        </Link>
      </div>
    </article>
  );
};

// "Weitere Themen" at the end of an article: every other published topic.
export const MoreTopics: React.FC<{ currentKey: string; lang: ArticleLang }> = ({ currentKey, lang }) => {
  const others = ARTICLE_TOPICS.filter((t) => t.key !== currentKey);
  if (others.length === 0) return null;
  return (
    <section className="mt-14" aria-labelledby="weitere-themen">
      <h2 id="weitere-themen" className="text-3xl font-display font-bold text-pstax-navy mb-6">
        {ARTICLES_UI[lang].moreTopics}
      </h2>
      <div className="grid md:grid-cols-2 gap-6">
        {others.map((t) => (
          <ArticleCard key={t.key} topic={t} lang={lang} headingLevel="h3" />
        ))}
      </div>
    </section>
  );
};

// Top row inside a Quick Click-Through's own header (the flows hide the site
// nav and own the viewport): breadcrumb + "Zum ausführlichen Artikel →".
export const FlowArticleBar: React.FC<{ topic: ArticleTopic; lang: ArticleLang }> = ({ topic, lang }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 pb-1 text-[12px] leading-5">
    <Breadcrumb crumbs={articleCrumbs(topic, lang, true)} lang={lang} className="min-w-0 text-pstax-gray" />
    <Link to={topic[lang].path} className="font-semibold text-pstax-link hover:underline underline-offset-2">
      {ARTICLES_UI[lang].toArticle}
    </Link>
  </div>
);

// Same link as a standalone line, for a click-through's last screen.
export const FlowArticleLink: React.FC<{ topic: ArticleTopic; lang: ArticleLang; className?: string }> = ({
  topic,
  lang,
  className = '',
}) => (
  <Link
    to={topic[lang].path}
    className={`inline-flex min-h-[40px] items-center text-[15px] font-semibold text-pstax-link hover:underline ${className}`}
  >
    {ARTICLES_UI[lang].toArticle}
  </Link>
);
